import React, { useState } from "react";
import { Box, TextField, IconButton, Typography, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PaymentsIcon from "@mui/icons-material/Payments";
import ClearIcon from "@mui/icons-material/Clear";
import { Order } from "../../../../Models/Order";
import { AdminAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import AdminOrderEditableDetailsSection from "../../../../components/PageComponents/AdminComponents/AdminOrderEditableDetailsSection/AdminOrderEditableDetailsSection";
import "./AdminManagePaymentsPage.scss";

const AdminManageOrdersPage: React.FC = () => {
  const [orderId, setOrderId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const clearResults = () => {
    setOrders([]);
    setStatusMessage("");
    setOrderId("");
    setBuyerId("");
    setSellerId("");
    setStartDate("");
    setEndDate("");
  };

  const fetchOrderById = async () => {
    if (!orderId.trim()) {
      setStatusMessage("Please enter an order ID");
      return;
    }
    
    setIsLoading(true);
    setStatusMessage("");
    try {
      const order = await AdminAPIHandler.shared.getOrderById(orderId);
      setOrders([order]);
      setStatusMessage(`Found 1 order`);
    } catch (error) {
      setOrders([]);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  const fetchOrdersByBuyerId = async () => {
    if (!buyerId.trim()) {
      setStatusMessage("Please enter a buyer ID");
      return;
    }
    
    setIsLoading(true);
    setStatusMessage("");
    try {
      const fetchedOrders = await AdminAPIHandler.shared.getOrdersByBuyerId(buyerId);
      setOrders(fetchedOrders);
      setStatusMessage(`Found ${fetchedOrders.length} order(s) for buyer`);
    } catch (error) {
      setOrders([]);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  const fetchOrdersBySellerId = async () => {
    if (!sellerId.trim()) {
      setStatusMessage("Please enter a seller ID");
      return;
    }
    
    setIsLoading(true);
    setStatusMessage("");
    try {
      const fetchedOrders = await AdminAPIHandler.shared.getOrdersBySellerId(sellerId);
      setOrders(fetchedOrders);
      setStatusMessage(`Found ${fetchedOrders.length} order(s) for seller`);
    } catch (error) {
      setOrders([]);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  const fetchOrdersByDateRange = async () => {
    if (!startDate || !endDate) {
      setStatusMessage("Please select both start and end dates");
      return;
    }

    setIsLoading(true);
    setStatusMessage("");
    try {
      const fetchedOrders = await AdminAPIHandler.shared.getOrdersByDateRange(startDate, endDate);
      setOrders(fetchedOrders);
      setStatusMessage(`Found ${fetchedOrders.length} order(s)`);
    } catch (error) {
      setOrders([]);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  const fetchPayableOrders = async () => {
    setIsLoading(true);
    setStatusMessage("");
    try {
      const fetchedOrders = await AdminAPIHandler.shared.getSellerPayableOrders();
      setOrders(fetchedOrders);
      setStatusMessage(`Found ${fetchedOrders.length} payable order(s)`);
    } catch (error) {
      setOrders([]);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Search Controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 3,
        p: 3,
        background: '#f8fafc',
        borderRadius: 2,
        border: '1px solid #e2e8f0'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1e293b' }}>
            🔍 Search Orders
          </Typography>
          {/* Fetch Payable Orders Button */}
          <IconButton
            onClick={fetchPayableOrders}
            disabled={isLoading}
            sx={{
              background: '#10b981',
              color: 'white',
              px: 2,
              borderRadius: 2,
              '&:hover': {
                background: '#059669'
              },
              '&:disabled': {
                background: '#e2e8f0',
                color: '#94a3b8'
              }
            }}
          >
            <PaymentsIcon sx={{ mr: 1 }} />
            <Typography variant="body2" fontWeight={600}>
              Fetch Seller Payable Orders
            </Typography>
          </IconButton>
        </Box>

        {/* Search by ID Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          background: 'white',
          borderRadius: 2,
          border: '1px solid #e2e8f0'
        }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Search by ID
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Order ID */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Order ID"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchOrderById()}
                disabled={isLoading}
                sx={{ minWidth: '180px' }}
              />
              <IconButton
                onClick={fetchOrderById}
                disabled={!orderId.trim() || isLoading}
                sx={{
                  background: '#3b82f6',
                  color: 'white',
                  '&:hover': { background: '#2563eb' },
                  '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>

            {/* Buyer ID */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Buyer ID"
                value={buyerId}
                onChange={(e) => setBuyerId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchOrdersByBuyerId()}
                disabled={isLoading}
                sx={{ minWidth: '180px' }}
              />
              <IconButton
                onClick={fetchOrdersByBuyerId}
                disabled={!buyerId.trim() || isLoading}
                sx={{
                  background: '#8b5cf6',
                  color: 'white',
                  '&:hover': { background: '#7c3aed' },
                  '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>

            {/* Seller ID */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Seller ID"
                value={sellerId}
                onChange={(e) => setSellerId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchOrdersBySellerId()}
                disabled={isLoading}
                sx={{ minWidth: '180px' }}
              />
              <IconButton
                onClick={fetchOrdersBySellerId}
                disabled={!sellerId.trim() || isLoading}
                sx={{
                  background: '#f59e0b',
                  color: 'white',
                  '&:hover': { background: '#d97706' },
                  '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Date Range Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          background: 'white',
          borderRadius: 2,
          border: '1px solid #e2e8f0'
        }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Search by Date Range
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="From"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isLoading}
              sx={{ minWidth: '160px' }}
            />
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>to</Typography>
            <TextField
              size="small"
              label="To"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isLoading}
              sx={{ minWidth: '160px' }}
            />
            <IconButton
              onClick={fetchOrdersByDateRange}
              disabled={!startDate || !endDate || isLoading}
              sx={{
                background: '#3b82f6',
                color: 'white',
                '&:hover': { background: '#2563eb' },
                '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
              }}
            >
              <DateRangeIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Status Message */}
      {statusMessage && (
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2, 
          background: statusMessage.startsWith('Error') ? '#fee2e2' : '#dcfce7',
          color: statusMessage.startsWith('Error') ? '#991b1b' : '#166534',
          borderRadius: 2,
          fontWeight: 500
        }}>
          <Typography>{statusMessage}</Typography>
          <Button
            startIcon={<ClearIcon />}
            onClick={clearResults}
            size="small"
            sx={{
              color: statusMessage.startsWith('Error') ? '#991b1b' : '#166534',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: statusMessage.startsWith('Error') ? '#fecaca' : '#bbf7d0'
              }
            }}
          >
            Clear Results
          </Button>
        </Box>
      )}

      {/* Orders Display */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {orders.map((order) => (
          <AdminOrderEditableDetailsSection key={order.id} order={order} />
        ))}
      </Box>

      {/* Empty State */}
      {!isLoading && orders.length === 0 && !statusMessage && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          color: '#94a3b8'
        }}>
          <Typography variant="h6" gutterBottom>
            No orders to display
          </Typography>
          <Typography variant="body2">
            Use the search options above to find orders
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminManageOrdersPage;
